import { useGetSellerCustomers } from '../../../api/lending/lapiHooks';

/**
 * Whether the seller has no customers at all, across every tab.
 *
 * Deliberately sends no `view`, `search` or pill filter, so the count spans
 * both tabs. A tab-scoped count cannot answer this: the tabs partition
 * customers by onboarding stage, so a seller whose customers have all finished
 * onboarding has zero on the In progress tab while still having customers.
 *
 * Callers share one query key, so the header and the tab body read a single
 * request between them rather than one each.
 *
 * `include_declined` is sent unconditionally, independent of the table setting:
 * the API's declined exclusion is scoped to the `spending_limit` view, so a
 * view-less request already counts declined buyers and the param is a no-op
 * here. It is passed to keep that independence explicit — a seller whose only
 * customers are declined must not be offered the first-run empty state, which
 * would hide the search box and the drawer that reveals them.
 *
 * Only `metadata.count` is read, never the rows. Narrowing the request to a
 * single row would need a backend change: the seller customers view paginates
 * on a hardcoded `paginate_by`, so the `page_size` in its OpenAPI schema is
 * accepted and ignored. `count` is the whole filtered queryset either way.
 */
export function useSellerHasCustomers(): {
  hasNoCustomers: boolean;
  loading: boolean;
} {
  const { metadata, loadingSellerCustomers } = useGetSellerCustomers({
    page: 1,
    include_declined: true,
  });

  return {
    hasNoCustomers: metadata?.count === 0,
    loading: loadingSellerCustomers,
  };
}
